import { Button, Typography, withForm, FormValues, SelectOption } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { Close, PlusCircleOutline } from 'mdi-material-ui';
import { useEffect } from 'react';

import { surveyFilterOptions, surveyStatusOptions } from '@/utils/filterOptions';

import { useFetchAllSurveyListQuery } from '../../../api';
import {
	generateSurveySelectOptions,
	hasCompletedSurvey,
	surveyFiltersDefaultObj,
} from '../../../utils';

import { patientListFiltersFormOptions } from './patientListFiltersFormOptions';
import { PatientListFiltersFormSection } from './PatientListFiltersFormSection';

export const PatientListFiltersSurvey = withForm({
	...patientListFiltersFormOptions,
	render: function Render({ form }) {
		// get data to populate select options
		const { data: surveyList, isSuccess: surveyListIsSuccess } = useFetchAllSurveyListQuery();
		const surveySelectOptions =
			surveyListIsSuccess && surveyList?.results
				? generateSurveySelectOptions(surveyList.results, true)
				: ([] as SelectOption[]);

		const values = useStore(form.store, (state) => state.values);
		const hasCompleted = hasCompletedSurvey(values as unknown as FormValues);

		useEffect(() => {
			// check if we need to reset needs assistance value after status changes
			if (!hasCompleted && values.survey_needs_assistance) {
				form.setFieldValue('survey_needs_assistance', '');
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [hasCompleted, values.survey_needs_assistance]);

		return (
			<PatientListFiltersFormSection
				title='Survey filters'
				dataTestId='filters-survey-section'
			>
				<>
					<form.Field name='survey_filters' mode='array'>
						{(field) => (
							<div>
								{(field.state.value || []).map((_survey_filter, index) => (
									<div
										key={index}
										className='grid grid-cols md:grid-cols-3 gap-4 md:gap-8 pb-4 items-start border border-secondaryBorder md:border-none px-4 md:px-0 mb-4'
									>
										<div className='flex items-end'>
											<div className='grow'>
												<form.AppField
													name={`survey_filters[${index}].filter_type`}
												>
													{(field) => (
														<field.SelectField
															label='Include/exclude'
															labelClassName='invisible'
															options={surveyFilterOptions}
														/>
													)}
												</form.AppField>
											</div>
											<div className='pb-3 pl-6'>
												<Typography>patient where</Typography>
											</div>
										</div>
										<div className='flex items-end'>
											<div className='grow'>
												<form.AppField
													name={`survey_filters[${index}].survey_id`}
												>
													{(field) => (
														<field.SelectField
															label='Survey name'
															options={surveySelectOptions}
														/>
													)}
												</form.AppField>
											</div>
											<div className='pb-3 pl-6'>
												<Typography>was</Typography>
											</div>
										</div>
										<div className='flex items-end'>
											<div className='grow'>
												<form.AppField
													name={`survey_filters[${index}].survey_status`}
												>
													{(field) => (
														<field.SelectField
															label='Survey status'
															labelClassName='invisible'
															options={surveyStatusOptions}
														/>
													)}
												</form.AppField>
											</div>
											<div className='pb-1 pl-4'>
												<Button
													variant='icon'
													size='icon'
													className='px-0 py-0 text-secondaryTextDark'
													onClick={() => field.removeValue(index)}
													title='Close'
												>
													<Close className='w-3 h-3' />
												</Button>
											</div>
										</div>
									</div>
								))}
								<div className='-ml-2'>
									<Button
										variant='icon'
										size='icon'
										className='px-0 py-0 text-primaryBrand'
										onClick={() => field.pushValue(surveyFiltersDefaultObj)}
										title='Add'
									>
										<PlusCircleOutline className='w-4 h-4' />
										<span className='text-secondaryTextDark pl-2 inline-block pt-px align-top'>
											Add
										</span>
									</Button>
								</div>
							</div>
						)}
					</form.Field>
					<div className='pt-4'>
						<form.AppField name='survey_needs_assistance'>
							{(field) => (
								<field.RadioField
									groupLabel='Patient completed survey with assistance from Patient Services?'
									className='col6'
									dataTestId='survey-needs-assistance-group'
									disabled={!hasCompleted}
									options={[
										{ label: 'All', value: '' },
										{ label: 'Yes', value: 'completed_with_assistance' },
										{ label: 'No', value: 'completed_no_assistance' },
									]}
								/>
							)}
						</form.AppField>
					</div>
					<Typography className='pt-6 pb-2 text-secondaryTextDark'>
						Dates will apply to all survey filters.
					</Typography>
					<div className='grid grid-cols md:grid-cols-3 gap-4 md:gap-8 mt-2 pb-5'>
						<div>
							<form.AppField
								name='survey_from_date'
								children={(field) => (
									<field.TextField label='Date from' type='date' />
								)}
							/>
						</div>
						<div>
							<form.AppField
								name='survey_to_date'
								children={(field) => (
									<field.TextField label='Date to' type='date' />
								)}
							/>
						</div>
					</div>
				</>
			</PatientListFiltersFormSection>
		);
	},
});
